import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  useDisclosure,
} from '@heroui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiMenu } from 'react-icons/fi';
import {
  FaTh,
  FaMobileAlt,
  FaStar,
  FaHeart,
  FaComments,
  FaBell,
  FaPlug,
  FaTicketAlt,
  FaSignOutAlt,
  FaCog,
  FaFileAlt,
  FaBoxOpen,
} from 'react-icons/fa';
import React from 'react';
import { useTranslation } from 'react-i18next';
import logo from '../../../assets/logo/main-logo.png';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { apiClient } from '../../../api';
import { ProfilePictureDisplay } from '../../components/ProfilePicture';
import {
  getProfilePictureUrlFromName,
  getUserProfileWithPicture,
} from '../../../service/profilePictureService';
import { useState, useEffect, useCallback } from 'react';
import { FaUserCircle } from 'react-icons/fa';
import CustomButton from '../../components/CustomButton';
import { handleLogout } from '../../components/auth/apiAuth';

const MobileSideBar = () => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const [userName, setUserName] = useState('User');
  const [profileImage, setProfileImage] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [loading, setLoading] = useState(false);

  const userId = auth.userId;

  const fetchUserData = useCallback(async () => {
    if (!userId || !auth.isAuthenticated) {
      console.log('Cannot fetch user data: No user ID or not authenticated');
      return;
    }

    try {
      setLoading(true);
      console.log(`Fetching user data for mobile sidebar, ID: ${userId}`);

      // Use the profile service to get user data with proper profile picture handling
      const profileData = await getUserProfileWithPicture(userId);

      if (profileData) {
        console.log(
          'User profile data fetched for mobile sidebar:',
          profileData
        );

        // Set user name from the profile data
        setUserName(
          profileData.firstName || profileData.lastName
            ? `${profileData.firstName ?? ''} ${profileData.lastName ?? ''}`.trim()
            : profileData.name || profileData.email?.split('@')[0] || 'User'
        );

        // Handle profile picture - prioritize profilePicture field
        const profileImageName =
          profileData.profileImage || profileData.profilePicture || '';
        if (profileImageName) {
          console.log('Found profile image name:', profileImageName);

          // If it's just an image name (not a full URL), convert it to full URL
          if (profileImageName && !profileImageName.startsWith('http')) {
            const fullUrl = getProfilePictureUrlFromName(profileImageName);
            setProfileImage(profileImageName); // Store the image name
            setProfileImageUrl(fullUrl); // Store the full URL
            console.log('Profile image URL generated:', fullUrl);
          } else {
            // If it's already a full URL, use it directly
            setProfileImage(profileImageName);
            setProfileImageUrl(profileImageName);
          }
        } else {
          // Clear profile image if none found
          setProfileImage('');
          setProfileImageUrl('');
        }
      }
    } catch (error) {
      console.error('Error fetching user data for mobile sidebar:', error);
      // Fallback to direct API call if profile service fails
      try {
        const response = await apiClient.get(`user/api/v1/user/${userId}`);
        if (response.status === 200 && response.data) {
          const userData = response.data.user || response.data;
          setUserName(
            userData.firstName || userData.lastName
              ? `${userData.firstName ?? ''} ${userData.lastName ?? ''}`.trim()
              : userData.name || userData.email?.split('@')[0] || 'User'
          );

          // Handle profile picture from direct API response
          const profileImageData =
            userData.profilePicture || userData.profileImage || '';
          if (profileImageData && !profileImageData.startsWith('http')) {
            const fullUrl = getProfilePictureUrlFromName(profileImageData);
            setProfileImage(profileImageData);
            setProfileImageUrl(fullUrl);
          } else if (profileImageData) {
            setProfileImage(profileImageData);
            setProfileImageUrl(profileImageData);
          }
        }
      } catch (fallbackError) {
        console.error('Fallback API call also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  }, [auth.isAuthenticated, userId]);

  // Get user name from auth context and fetch user data
  useEffect(() => {
    if (!auth.loading) {
      if (auth.isAuthenticated && auth.user) {
        console.log('User profile in mobile sidebar:', auth.user);

        // Try to get user's name from different possible profile fields
        const name = auth.user.name || auth.user.email?.split('@')[0] || 'User';

        console.log('Using name for mobile sidebar:', name);
        setUserName(name);

        // Fetch user data including profile image
        fetchUserData();
      } else {
        console.log('User not authenticated in mobile sidebar');
        setUserName(t('customer_sidebar.guest_user'));
        setProfileImage('');
        setProfileImageUrl('');
      }
    }
  }, [auth.loading, auth.isAuthenticated, auth.user, fetchUserData]);

  // Listen for profile picture updates from localStorage or custom events
  useEffect(() => {
    const handleProfilePictureUpdate = () => {
      console.log(
        'Profile picture update detected, refreshing mobile sidebar data'
      );
      fetchUserData();
    };

    // Listen for custom profile picture update events
    window.addEventListener(
      'profilePictureUpdated',
      handleProfilePictureUpdate
    );

    // Also listen for storage events (if profile updates are stored in localStorage)
    window.addEventListener('storage', (e) => {
      if (e.key === 'profilePictureUpdated') {
        handleProfilePictureUpdate();
      }
    });

    return () => {
      window.removeEventListener(
        'profilePictureUpdated',
        handleProfilePictureUpdate
      );
      window.removeEventListener('storage', handleProfilePictureUpdate);
    };
  }, [fetchUserData]);

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  const isPathActive = (currentPath: string, path?: string) => {
    if (!path) return false;
    return currentPath === path || currentPath.startsWith(path + '/');
  };

  const handleNavigateToHome = () => {
    navigate('/');
  };

  const menuItems = [
    {
      name: t('customer_sidebar.dashboard'),
      path: '/customer/customer-dashboard',
      icon: <FaTh />,
    },
    {
      name: t('customer_sidebar.bookings'),
      path: '/customer/customer-booking',
      icon: <FaMobileAlt />,
    },
    {
      name: t('customer_sidebar.job_requests'),
      path: '/customer/customer-job-requests',
      icon: <FaFileAlt />,
    },
    {
      name: t('customer_sidebar.my_packages'),
      path: '/customer/customer-packages',
      icon: <FaBoxOpen />,
    },
    {
      name: t('customer_sidebar.reviews'),
      path: '/customer/customer-reviews',
      icon: <FaStar />,
    },
    {
      name: t('customer_sidebar.favorite_services'),
      path: '/customer/customer-favorite-services',
      icon: <FaHeart />,
    },
    {
      name: t('customer_sidebar.chat'),
      path: '/customer/customer-chat',
      icon: <FaComments />,
    },
    {
      name: t('customer_sidebar.notifications'),
      path: '/customer/customer-notifications',
      icon: <FaBell />,
    },
    {
      name: t('customer_sidebar.support_tickets'),
      path: '/customer/customer-support-tickets',
      icon: <FaTicketAlt />,
    },
  ];

  const settingsItems = [
    {
      name: t('customer_sidebar.account_settings'),
      path: '/customer/customer-profile',
      icon: <FaCog />,
    },
    {
      name: t('customer_sidebar.connected_apps'),
      path: '/customer/settings/connected-apps',
      icon: <FaPlug />,
    },
  ];

  return (
    <>
      <FiMenu
        onClick={onOpen}
        className="cursor-pointer hover:text-primary transition-colors w-8 h-8 text-gray-600"
        aria-label={t('customer_sidebar.open_menu')}
      />

      <Drawer
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        radius="none"
        size="xs"
        placement="left"
      >
        <DrawerContent className="bg-[#0f1419]">
          {(onClose) => (
            <>
              <DrawerHeader className="flex flex-col gap-2 border-b border-gray-600 bg-[#0f1419] items-center justify-center">
                <div className="flex flex-col items-center justify-center w-full">
                  <Link
                    to="/"
                    onClick={onClose}
                    className="flex justify-center"
                  >
                    <img
                      src={logo}
                      alt={t('customer_sidebar.logo_alt')}
                      className="object-contain h-12 w-auto"
                    />
                  </Link>
                  <p className="text-[11px] text-gray-400 tracking-widest uppercase mt-2 text-center">
                    {t('customer_sidebar.customer_dashboard')}
                  </p>
                </div>
              </DrawerHeader>

              <DrawerBody className="p-0 overflow-y-auto bg-[#0f1419]">
                <nav className="flex flex-col">
                  <div className="px-4 py-3">
                    <h4 className="text-[11px] uppercase tracking-wide text-slate-500 mb-3">
                      {t('customer_sidebar.general')}
                    </h4>
                    <ul className="space-y-1">
                      {menuItems.map((item, index) => (
                        <button
                          key={index}
                          onClick={() => handleNavigate(item.path)}
                          className={`flex items-center gap-3 px-3 py-2 text-sm transition-colors w-full text-left rounded-md ${
                            isPathActive(location.pathname, item.path)
                              ? 'bg-primary/20 text-white font-semibold border-l-4 border-primary'
                              : 'hover:bg-white/5 text-gray-400 hover:text-white'
                          }`}
                        >
                          <span
                            className={`text-lg ${
                              isPathActive(location.pathname, item.path)
                                ? 'text-secondary'
                                : 'text-gray-400'
                            }`}
                          >
                            {item.icon}
                          </span>
                          <span className="flex-1">{item.name}</span>
                        </button>
                      ))}
                    </ul>
                  </div>

                  <div className="px-4 py-3 border-t border-gray-600">
                    <h4 className="text-[11px] uppercase tracking-wide text-slate-500 mb-3">
                      {t('customer_sidebar.settings')}
                    </h4>
                    <ul className="space-y-1">
                      {settingsItems.map((item, index) => (
                        <button
                          key={index}
                          onClick={() => handleNavigate(item.path)}
                          className={`flex items-center gap-3 px-3 py-2 text-sm transition-colors w-full text-left rounded-md ${
                            isPathActive(location.pathname, item.path)
                              ? 'bg-primary/20 text-white font-semibold border-l-4 border-primary'
                              : 'hover:bg-white/5 text-gray-400 hover:text-white'
                          }`}
                        >
                          <span
                            className={`text-lg ${
                              isPathActive(location.pathname, item.path)
                                ? 'text-secondary'
                                : 'text-gray-400'
                            }`}
                          >
                            {item.icon}
                          </span>
                          <span className="flex-1">{item.name}</span>
                        </button>
                      ))}
                    </ul>
                  </div>

                  <div className="px-4 py-3 border-t border-gray-600 mt-auto">
                    <div
                      className="flex flex-wrap items-center cursor-pointer"
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        handleNavigate('/customer/customer-profile')
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleNavigate('/customer/customer-profile');
                        }
                      }}
                    >
                      <div className="w-10 h-10 rounded-md overflow-hidden">
                        {loading ? (
                          <div className="w-full h-full flex items-center justify-center bg-gray-200 animate-pulse">
                            <FaUserCircle className="text-gray-400 text-2xl" />
                          </div>
                        ) : (
                          <ProfilePictureDisplay
                            profileImage={profileImage}
                            profileImageUrl={profileImageUrl}
                            userName={userName}
                            size="sm"
                            className="w-full h-full"
                          />
                        )}
                      </div>
                      <div className="ml-4">
                        <p className="text-sm text-gray-200 font-medium">
                          {userName}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {t('customer_sidebar.active_account')}
                        </p>
                      </div>
                    </div>

                    <div className="block laptop:hidden  border-gray-600 pb-2">
                      <CustomButton
                        label={t('customer_sidebar.home')}
                        variant="flat"
                        color="primary"
                        fullWidth
                        endContent={<FaSignOutAlt />}
                        className="mt-10"
                        onPress={handleNavigateToHome}
                      />

                      <CustomButton
                        label="Sign Out"
                        variant="flat"
                        color="danger"
                        fullWidth
                        endContent={<FaSignOutAlt />}
                        className="mt-5"
                        onPress={handleLogout}
                      />

                      <div className="px-2 pb-4 text-center space-y-2 mt-5">
                        <p className="text-[11px] text-gray-500 font-medium leading-relaxed flex items-baseline gap-1">
                          <span className="inline-flex">
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 30 20"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M30 0H0V20H30V0Z" fill="#F0F0F0" />
                              <path d="M10 0H0V20H10V0Z" fill="#D80027" />
                              <path d="M30 0H20V20H30V0Z" fill="#D80027" />
                              <path
                                d="M16.875 11.3889L18.75 10.4514L17.8125 9.98275V9.04526L15.9375 9.98275L16.875 8.10777H15.9375L15 6.70148L14.0625 8.10777H13.125L14.0625 9.98275L12.1875 9.04526V9.98275L11.25 10.4514L13.125 11.3889L12.6563 12.3264H14.5313V13.7327H15.4688V12.3264H17.3438L16.875 11.3889Z"
                                fill="#D80027"
                              />
                            </svg>
                          </span>

                          <span>{t('customer_sidebar.proudly_canadian')}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </nav>
              </DrawerBody>
            </>
          )}
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default React.memo(MobileSideBar);
